<script setup>
import ClickToActionSection from '@/components/ClickToActionSection.vue'
import { BeakerIcon } from '@heroicons/vue/24/solid'
import { index as contactIndex } from '@/actions/App/Http/Controllers/ContactController'
import { index as projectIndex } from '@/actions/App/Http/Controllers/ProjectController'
import ProjectCard from '@/pages/projects/partials/ProjectCard.vue'
import ServiceCard from '@/pages/services/Partials/ServiceCard.vue'

defineProps({
    projects: {
        type: Array,
        required: true,
    },

    serviceName: {
        type: String,
        required: true,
    },
})

const seo = {
    title: 'Vodoinstalace',
    description:
        'Profesionální vodoinstalatérské služby - instalace rozvodů, opravy, rekonstrukce koupelen a odpadních systémů. Kvalitní materiály a spolehlivé řešení od firmy Kalianko.',
}

const services = [
    {
        name: 'Kompletní instalace rozvodů',
        description: 'Odborná instalace vodovodních rozvodů pro novostavby i rekonstrukce.',
        icon: BeakerIcon,
        features: [
            'Instalace v bytech, domech i komerčních objektech',
            'Návrh efektivního vedení rozvodů',
            'Kvalitní a prověřené materiály',
            'Precizní provedení a kontrola funkčnosti',
            'Dlouhá životnost a spolehlivost',
        ],
    },
    {
        name: 'Opravy a servis',
        description: 'Rychlé a spolehlivé řešení instalatérských problémů.',
        icon: BeakerIcon,
        features: [
            'Opravy běžných i složitějších závad',
            'Kapající baterie, potrubí, odpady, ventily',
            'Rychlý zásah při haváriích',
            'Minimalizace škod',
            'Diagnostika a trvalé řešení',
        ],
    },
    {
        name: 'Montáž sanitární techniky',
        description: 'Profesionální instalace s důrazem na funkčnost i vzhled.',
        icon: BeakerIcon,
        features: [
            'Montáž umyvadel, van, sprch, WC a dřezů',
            'Správné a bezpečné napojení',
            'Kvalitní technické provedení',
            'Důraz na estetiku',
            'Poradenství při výběru',
        ],
    },

    {
        name: 'Výměna a modernizace rozvodů',
        description: 'Modernizace rozvodů pro vyšší bezpečnost a komfort.',
        icon: BeakerIcon,
        features: [
            'Výměna zastaralého potrubí',
            'Moderní materiály (plast, měď)',
            'Splnění aktuálních standardů',
            'Zlepšení kvality vody',
            'Snížení rizika havárií',
        ],
    },
]

const benefits = [
    { value: '24h', label: 'Havarijní služba' },
    { value: '30+', label: 'Let zkušeností' },
    { value: '100%', label: 'Záruka na práci' },
]
</script>

<template>
    <Head :title="seo.title">
        <meta
            name="description"
            :content="seo.description"
        />
    </Head>

    <section>
        <div class="relative isolate overflow-hidden bg-gray-900">
            <img
                src="/images/kalianko-team.png"
                alt="Vodoinstalace - profesionální instalatérské služby"
                class="absolute inset-0 -z-10 size-full object-cover opacity-20"
            />
            <div class="absolute inset-0 -z-10 bg-linear-to-b from-gray-900/50 via-gray-900/80 to-gray-900" />

            <div class="mx-auto max-w-4xl px-6 py-32 text-center sm:py-40 lg:px-8 lg:py-56">
                <h1 class="text-4xl font-bold tracking-tight text-white sm:text-6xl">Vodoinstalace</h1>

                <p class="mx-auto mt-6 max-w-2xl text-lg/8 text-gray-300">
                    Profesionální instalatérské služby pro váš domov i firmu. Od oprav kapajícího kohoutku po kompletní
                    instalace vodovodních systémů.
                </p>

                <div class="mt-10 flex flex-col items-center justify-center gap-2 lg:flex-row">
                    <Link
                        :href="contactIndex().url + '#demand-form'"
                        class="primary-link inline-flex w-full items-center justify-center rounded-full text-lg lg:w-fit"
                    >
                        Nezávazná poptávka
                    </Link>
                    <Link
                        :href="projectIndex().url + '?service=' + serviceName"
                        class="inline-flex w-full items-center justify-center rounded-full border border-white/30 bg-white/10 px-6 py-3 text-lg font-semibold text-white backdrop-blur-lg transition hover:bg-white/20 sm:w-auto"
                    >
                        Naše realizace
                    </Link>
                </div>

                <!-- Quick benefits -->
                <div class="mt-16 grid grid-cols-3 gap-4 border-t border-white/10 pt-10">
                    <div
                        v-for="benefit in benefits"
                        :key="benefit.label"
                        class="text-center"
                    >
                        <div class="text-brand-primary text-3xl font-bold">{{ benefit.value }}</div>
                        <div class="mt-1 text-sm text-gray-400">{{ benefit.label }}</div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Features Section -->
        <div class="section-width space-y-20 px-2 py-24 sm:py-32 lg:px-0">
            <div class="mx-auto max-w-2xl text-center">
                <p class="text-brand-primary text-sm font-semibold tracking-wider uppercase">Naše služby</p>
                <h2 class="heading-2 mt-2">Co pro vás můžeme udělat</h2>
                <p class="paragraph mt-4">
                    Nabízíme kompletní vodoinstalační služby od návrhu až po realizaci. Každou zakázku bereme osobně.
                </p>
            </div>

            <div class="mt-16 grid grid-cols-1 gap-8 lg:grid-cols-3">
                <ServiceCard
                    v-for="service in services"
                    :key="service.name"
                    :service="service"
                />
            </div>

            <!-- <div class="space-y-24">
                <div
                    v-for="(feature, featureIdx) in services"
                    :key="feature.name"
                    class="flex flex-col gap-8 lg:flex-row lg:items-center lg:gap-16"
                >
                    <div class="mx-auto text-center lg:w-1/2">
                        <div class="flex items-center justify-center gap-4">
                            <div
                                class="bg-brand-primary/10 text-brand-primary flex size-12 shrink-0 items-center justify-center rounded-xl"
                            >
                                <WrenchScrewdriverIcon class="size-6" />
                            </div>
                            <span class="text-brand-primary text-sm font-medium">{{
                                String(featureIdx + 1).padStart(2, '0')
                            }}</span>
                        </div>

                        <h3 class="mt-4 text-2xl font-semibold tracking-tight text-gray-900">
                            {{ feature.name }}
                        </h3>

                        <p class="mt-4 text-base/7 text-gray-600">
                            {{ feature.description }}
                        </p>

                        <div class="mt-6">
                            <Link
                                :href="contactIndex().url + '#demand-form'"
                                class="inline-flex w-full items-center justify-center rounded-full border border-sky-500/30 bg-sky-500/10 px-6 py-3 font-semibold text-sky-500 backdrop-blur-lg transition hover:bg-sky-500/20 sm:w-auto"
                            >
                                Mám zájem o tuto službu
                            </Link>
                        </div>
                    </div>
                </div>
            </div> -->

            <ClickToActionSection />

            <div
                v-if="projects.length > 0"
                class="space-y-5"
            >
                <div class="mx-auto max-w-2xl lg:mx-0">
                    <h2 class="heading-2">Projekt z vodoinstalace</h2>
                    <p class="paragraph mt-4">
                        Podívejte se na některé z našich nedávných realizací v oblasti vodoinstalace. Každý projekt je
                        pro nás výzvou k předvedení našich schopností a odbornosti.
                    </p>
                </div>
                <div class="grid auto-rows-fr grid-cols-1 gap-8 lg:grid-cols-3">
                    <div
                        v-for="project in projects"
                        :key="project.id"
                        class="group"
                    >
                        <ProjectCard :project="project" />
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>
